import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export function CopyButton({ value, className, size = 'sm', showToast = true }) {
  const [copied, setCopied] = useState(false);
  const { t } = useLanguage();

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (showToast) {
        toast.success(t('common.copied') || 'Copied!');
      }
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      if (showToast) toast.success(t('common.copied') || 'Copied!');
      setTimeout(() => setCopied(false), 1500);
    }
  }, [value, showToast, t]);

  const iconSize = size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <button
      onClick={handleCopy}
      data-testid="copy-button"
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors duration-150',
        'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/5',
        size === 'xs' ? 'p-0.5' : 'p-1',
        className
      )}
      title={copied ? (t('common.copied') || 'Copied!') : (t('common.copy') || 'Copy')}
    >
      {copied ? (
        <Check className={cn(iconSize, 'text-[#2DD36F]')} />
      ) : (
        <Copy className={iconSize} />
      )}
    </button>
  );
}

export function CopyableText({ value, fullValue, className, mono = true, truncate = false, maxWidth }) {
  const copyValue = fullValue || value;
  return (
    <span className={cn('inline-flex items-center gap-1 group', className)}>
      <span className={cn(
        mono && 'font-mono',
        truncate && 'truncate',
        maxWidth && `max-w-[${maxWidth}]`
      )}>
        {value}
      </span>
      <CopyButton
        value={copyValue}
        size="xs"
        showToast={true}
        className="opacity-40 group-hover:opacity-100 transition-opacity duration-150"
      />
    </span>
  );
}
